import { Navigate, useLocation } from 'react-router-dom';
import { useAuth, UserRole } from '@/hooks/useAuth';
import { useAuthContext } from '@/contexts/AuthContext';
import { Loader2 } from 'lucide-react';
import { USER_ROLES, getDefaultRoute } from '@/config/roles';
import { useState, useEffect } from 'react';
import { logger } from '@/lib/logger';
import { usePortal } from '@/hooks/usePortal';
import { useAccountStatusQuery } from '@/hooks/queries/useAccountStatusQuery';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole: UserRole;
}

export function ProtectedRoute({ children, requiredRole }: ProtectedRouteProps) {
  const { user, hasRole, loading, roles, rolesError } = useAuth();
  const { currentRole } = useAuthContext();
  const location = useLocation();
  const { currentPortal } = usePortal();
  const hasRequiredRole = hasRole(requiredRole);
  const isAdmin = hasRole(USER_ROLES.ADMIN);

  // Fetch account status for pending_review / locked checks
  const { data: accountStatus, isLoading: accountStatusLoading } = useAccountStatusQuery(user?.id ?? null);

  // IMPORTANT: Avoid redirect loops.
  // If a user is authenticated but roles are temporarily empty (slow network / cache miss),
  // we should keep showing a stable loading state rather than "timing out" and redirecting.
  //
  // We still add a long safety timeout to surface a helpful message (not a redirect).
  const [rolesSafetyTimedOut, setRolesSafetyTimedOut] = useState(false);
  const ROLES_SAFETY_TIMEOUT_MS = 8000; // 8s: balance between cold starts and UX

  useEffect(() => {
    if (!user) {
      setRolesSafetyTimedOut(false);
      return;
    }
    if (roles.length > 0) {
      setRolesSafetyTimedOut(false);
      return;
    }
    // Only start the timer when we're authenticated and roles are empty.
    const t = setTimeout(() => {
      logger.warn('[ProtectedRoute] Roles still empty after safety timeout', { userId: user.id });
      setRolesSafetyTimedOut(true);
    }, ROLES_SAFETY_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [user, roles.length]);

  // Show loading while:
  // - session/roles are loading OR
  // - user exists but roles are empty (common right after sign-in) - skip when roles query failed
  // - user exists but account status is still loading (pending_review check)
  const isShowingLoading =
    loading ||
    (user && roles.length === 0 && !rolesSafetyTimedOut && !rolesError) ||
    (user && accountStatusLoading);
  const shouldRedirectToAuth = !user && !loading;

  // Show loading while auth is being determined
  // CRITICAL FIX: Also wait if user exists but roles are empty (might still be loading)
  // This prevents redirect loops when roles query is still in progress after sign-in
  // We give it a small grace period to allow roles to load after session is established
  // 
  // IMPORTANT: If loading is true OR if we're fetching (session or roles), show loading
  // This handles the case where queries are refetching after invalidation
  if (isShowingLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <Loader2 className="mx-auto mb-2 size-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading your account...</p>
        </div>
      </div>
    );
  }

  // If the user is authenticated but roles are still empty (timeout or query failed),
  // show a non-looping fallback instead of redirecting back to /auth (which can cause loops).
  if (user && roles.length === 0 && (rolesSafetyTimedOut || rolesError)) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="max-w-md px-6 text-center">
          <p className="text-base font-semibold text-foreground">We’re still loading your access…</p>
          <p className="mt-2 text-sm text-muted-foreground">
            Your account is signed in, but we couldn’t load roles yet. This is usually a temporary network / permissions issue.
          </p>
          <p className="mt-4 text-xs text-muted-foreground">
            If this persists, sign out and sign in again, or contact support.
          </p>
          <a
            href="/auth"
            className="mt-4 inline-block rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            onClick={async (e) => {
              e.preventDefault();
              const { supabase } = await import('@/integrations/supabase/client');
              await supabase.auth.signOut();
              window.location.href = '/auth';
            }}
          >
            Sign out and try again
          </a>
        </div>
      </div>
    );
  }

  // Redirect if not authenticated
  // CRITICAL: Only redirect if we're sure loading is complete AND user is null
  // Don't redirect if we just came from a sign-in (give it a moment for session to establish)
  if (shouldRedirectToAuth) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // Pending review: redirect to /auth/pending
  if (user && accountStatus?.account_status === 'pending_review') {
    return <Navigate to="/auth/pending" replace />;
  }

  // Locked account: block access
  if (user && accountStatus?.locked_at) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="max-w-md px-6 text-center">
          <p className="text-base font-semibold text-foreground">Account Locked</p>
          <p className="mt-2 text-sm text-muted-foreground">
            Your account has been locked. Please contact support.
          </p>
        </div>
      </div>
    );
  }

  // ADMIN ACTING AS ROLE: Allow admin to access buyer/supplier portals when switched to that role
  // Admin can always access admin portal
  if (isAdmin) {
    // Check localStorage for acting role (in case currentRole hasn't loaded yet)
    const storedRole = typeof window !== 'undefined' 
      ? localStorage.getItem('holescale_active_role') as UserRole | null
      : null;
    const actingRole = currentRole || storedRole;
    
    // If admin is acting as buyer/supplier, allow access to that portal
    if (requiredRole === 'buyer' && (actingRole === 'buyer' || storedRole === 'buyer')) {
      return <>{children}</>;
    }
    if (requiredRole === 'supplier' && (actingRole === 'supplier' || storedRole === 'supplier')) {
      return <>{children}</>;
    }
    // Admin can always access admin portal
    if (requiredRole === 'admin') {
      return <>{children}</>;
    }
    // If admin is in admin mode but trying to access buyer/supplier portal,
    // redirect to admin dashboard (they need to switch roles first)
    if (requiredRole === 'buyer' || requiredRole === 'supplier') {
      logger.info('[ProtectedRoute] Admin accessing portal - redirecting to admin dashboard. Switch role to access portal.', { 
        currentRole, 
        storedRole, 
        requiredRole 
      });
      return <Navigate to="/admin/dashboard" replace />;
    }
  }

  // PORTAL ENFORCEMENT: Check pathname and portal context to ensure strict portal separation
  // Multi-role users can switch portals, but features are restricted to current portal
  const pathname = location.pathname;
  const isBuyerRoute = pathname.startsWith('/buyer/');
  const isSupplierRoute = pathname.startsWith('/supplier/');
  const isAdminRoute = pathname.startsWith('/admin/');
  
  // Strict portal enforcement:
  // - If accessing buyer portal, must have buyer role
  // - If accessing supplier portal, must have supplier role
  // - Multi-role users can access both, but features are filtered by current portal
  
  if (isBuyerRoute) {
    if (!hasRole(USER_ROLES.BUYER)) {
      // User doesn't have buyer role - redirect to their appropriate portal
      if (hasRole(USER_ROLES.SUPPLIER)) {
        logger.warn('[ProtectedRoute] Buyer portal access denied - redirecting supplier to supplier portal');
        return <Navigate to={getDefaultRoute(USER_ROLES.SUPPLIER)} replace />;
      }
      // No valid role - redirect to auth
      return <Navigate to="/auth" replace />;
    }
    // User has buyer role - allow access to buyer portal
    return <>{children}</>;
  }
  
  if (isSupplierRoute) {
    if (!hasRole(USER_ROLES.SUPPLIER)) {
      // User doesn't have supplier role - redirect to their appropriate portal
      if (hasRole(USER_ROLES.BUYER)) {
        logger.warn('[ProtectedRoute] Supplier portal access denied - redirecting buyer to buyer portal');
        return <Navigate to={getDefaultRoute(USER_ROLES.BUYER)} replace />;
      }
      // No valid role - redirect to auth
      return <Navigate to="/auth" replace />;
    }
    // User has supplier role - allow access to supplier portal
    return <>{children}</>;
  }
  
  // For admin routes, check required role
  if (isAdminRoute && hasRequiredRole) {
    return <>{children}</>;
  }

  // Check if user has the required role for other routes
  if (hasRequiredRole) {
    return <>{children}</>;
  }
  
  // Use getDefaultRoute() for consistent redirect logic - only if user doesn't have access to requested portal
  if (hasRole(USER_ROLES.SUPPLIER)) {
    return <Navigate to={getDefaultRoute(USER_ROLES.SUPPLIER)} replace />;
  }
  if (hasRole(USER_ROLES.BUYER)) {
    return <Navigate to={getDefaultRoute(USER_ROLES.BUYER)} replace />;
  }
  if (hasRole(USER_ROLES.SUPPORT)) {
    return <Navigate to={getDefaultRoute(USER_ROLES.SUPPORT)} replace />;
  }
  
  // No valid roles but user is authenticated - redirect to auth
  // This handles cases where user just signed up and hasn't been assigned a role yet
  // or roles query completed but returned empty array
  return <Navigate to="/auth" replace />;
}
